import { useState, type ReactElement } from 'react';
import {
  DIASHOW_DAUER,
  DIASHOW_UEBERGANG,
  type Op,
  type TransitionKind,
  diashowLaenge,
  diashowPlan,
  sortedClips,
} from '@schnitt/shared';
import { useEditor } from '../store';
import { useZeitText } from '../zeit';
import { Sheet } from './Sheet';

/**
 * Eine Diashow aus Bildern (S23). Sascha: „Mit Dauer, Übergang und Bewegung.“
 *
 * Drei Fragen und ein Knopf. Was danach auf der Zeitleiste liegt, sind
 * gewöhnliche Clips -- jeder einzeln zu verschieben, zu kürzen und wieder
 * herauszunehmen. Deshalb steht hier auch kein „Diashow bearbeiten“: es gibt
 * danach keine Diashow mehr, es gibt einen Schnitt.
 */

/** Drei Blenden, nicht siebzehn: für eine Diashow sind das die, die man nimmt. */
const BLENDEN: { kind: TransitionKind | null; name: string }[] = [
  { kind: 'crossfade', name: 'Blende' },
  { kind: 'dipToBlack', name: 'Über Schwarz' },
  { kind: null, name: 'Hart' },
];

export function DiashowSheet({ onClose }: { onClose: () => void }): ReactElement {
  const zeit = useZeitText();
  const assets = useEditor((s) => s.assets);
  const timeline = useEditor((s) => s.timeline);
  const dispatch = useEditor((s) => s.dispatch);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);

  const [dauer, setDauer] = useState(DIASHOW_DAUER);
  const [blende, setBlende] = useState<TransitionKind | null>('crossfade');
  const [bewegung, setBewegung] = useState(true);
  const [ausgewaehlt, setAusgewaehlt] = useState<string[] | null>(null);

  // Nur fertige Bilder: eines, das noch eingelesen wird, hat weder Masse noch
  // Datei, und ohne Masse lässt sich nicht sagen, wie es den Rahmen füllt.
  const bilder = assets.filter((a) => a.kind === 'image' && a.status === 'ready' && a.width > 0);
  // Ohne Auswahl sind alle gemeint -- das ist der häufige Fall, und ein Blatt,
  // in dem man erst zwanzigmal tippen muss, hilft niemandem.
  const gewaehlt = ausgewaehlt ?? bilder.map((b) => b.id);
  const dabei = bilder.filter((b) => gewaehlt.includes(b.id));

  const umschalten = (id: string) =>
    setAusgewaehlt(gewaehlt.includes(id) ? gewaehlt.filter((x) => x !== id) : [...gewaehlt, id]);

  const spur = timeline.tracks.find((t) => t.kind === 'video');
  const clips = spur ? sortedClips(spur) : [];
  const hinten = clips.length ? clips.at(-1)!.start + clips.at(-1)!.duration : 0;
  const laenge = diashowLaenge(dabei.length, dauer, blende ? DIASHOW_UEBERGANG : 0);

  const bauen = () => {
    if (!spur || dabei.length === 0) return;
    const neu = diashowPlan(
      dabei.map((b) => ({ assetId: b.id, name: b.name, width: b.width, height: b.height })),
      {
        rahmen: { width: timeline.settings.width, height: timeline.settings.height },
        dauer,
        uebergang: blende ? DIASHOW_UEBERGANG : 0,
        kind: blende ?? 'crossfade',
        bewegung,
        ab: hinten,
      },
    );
    // Ein Schritt und nicht n: ein Rückgängig nimmt die ganze Show zurück,
    // nicht das letzte Bild von zwanzig.
    dispatch(neu.map((clip) => ({ op: 'addClip', trackId: spur.id, clip })) as Op[]);
    toast('success', `${neu.length} Bilder als Diashow eingefügt (${zeit(laenge)}).`);
    onClose();
  };

  return (
    <Sheet title="Diashow aus Bildern" onClose={onClose}>
      {bilder.length === 0 ? (
        <p className="hint">In diesem Projekt liegt noch kein fertiges Bild.</p>
      ) : (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Wie lange steht ein Bild?</h3>
          <div className="chips" data-diashow-dauer={String(dauer)}>
            {[2, 3, 4, 6, 8].map((d) => (
              <button key={d} className={`chip ${dauer === d ? 'active' : ''}`} onClick={() => setDauer(d)}>
                {d} s
              </button>
            ))}
          </div>

          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Übergang</h3>
          <div className="chips" data-diashow-blende={blende ?? 'hart'}>
            {BLENDEN.map((b) => (
              <button
                key={b.name}
                className={`chip ${blende === b.kind ? 'active' : ''}`}
                onClick={() => setBlende(b.kind)}
              >
                {b.name}
              </button>
            ))}
          </div>

          <div className="row" style={{ marginTop: 18 }}>
            <div className="grow">
              <div className="title">Bewegung</div>
              <div className="sub">
                {bewegung
                  ? 'Jedes Bild fährt langsam heran oder hinaus — abwechselnd, damit es nicht pumpt.'
                  : 'Die Bilder stehen still.'}
              </div>
            </div>
            <button
              className={`small ${bewegung ? '' : 'primary'}`}
              data-diashow-bewegung={String(bewegung)}
              onClick={() => setBewegung(!bewegung)}
            >
              {bewegung ? 'Aus' : 'An'}
            </button>
          </div>

          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>
            Bilder ({dabei.length} von {bilder.length})
          </h3>
          <div className="list" data-diashow-bilder={String(dabei.length)}>
            {bilder.map((b) => (
              <div className="row" key={b.id}>
                {b.posterUrl ? (
                  <img
                    src={b.posterUrl}
                    alt=""
                    style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 4, flex: '0 0 auto' }}
                  />
                ) : null}
                <div className="grow">
                  <div className="title">{b.name}</div>
                  <div className="sub">
                    {b.width}×{b.height}
                  </div>
                </div>
                <button className={`small ${gewaehlt.includes(b.id) ? 'primary' : ''}`} onClick={() => umschalten(b.id)}>
                  {gewaehlt.includes(b.id) ? 'dabei' : 'nicht'}
                </button>
              </div>
            ))}
          </div>

          <button
            className="primary"
            style={{ width: '100%', marginTop: 16 }}
            data-diashow-bauen
            onClick={bauen}
            disabled={role === 'viewer' || dabei.length === 0}
          >
            {dabei.length} Bilder einfügen ({zeit(laenge)})
          </button>
          <p className="hint">
            Sie kommen ans Ende der ersten Bildspur. Danach ist jedes ein gewöhnlicher Clip.
          </p>
        </>
      )}
    </Sheet>
  );
}
